import React, { useState, useEffect, useCallback } from "react";
import axios from "axios"; // Keep axios for polling
import { QRCodeSVG } from "qrcode.react";
import { FaQrcode, FaCopy } from "react-icons/fa"; // Example icons
import "../styles/GameDisplay.css";

// Helper function to get API base URL (adjust as needed)
const getApiBaseUrl = () =>
  process.env.REACT_APP_API_URL || "http://localhost:3000"; // Or your backend URL

// Component to display game data, received as prop initially, polls for updates
// ---> Accepts full 'game' object as prop again <---
function GameDisplay({
  game: initialGame,
  isOwner,
  isPlayer,
  qualifiedSubmissions,
  stepTimeRemaining,
}) {
  // ---> State holds the potentially updated game data <---
  const [game, setGame] = useState(initialGame);
  // ---> Remove internal loading/error states related to initial fetch <---
  // const [loading, setLoading] = useState(!initialGame);
  // const [error, setError] = useState(null);
  const [pollingError, setPollingError] = useState(null); // Separate state for polling errors
  const [showQr, setShowQr] = useState(false);

  // gameId derived from the game prop
  const gameId = game?.id;
  const gameUrl = gameId ? `${window.location.origin}/game/${gameId}` : "";

  // ---> Function to fetch updates (used by polling) <---
  const fetchGameUpdates = useCallback(async () => {
    if (!gameId) return; // Don't poll if we somehow lost the ID

    try {
      setPollingError(null); // Clear previous polling errors
      const response = await axios.get(
        `${getApiBaseUrl()}/api/games/${gameId}`
      );
      if (response.data && response.data.gameExists && response.data.game) {
        // ---> Update state only if fetched data differs? (Optional optimization)
        // For simplicity now, always update with fetched data
        setGame(response.data.game);
      } else {
        console.warn(
          "Polling received unexpected game data structure:",
          response.data
        );
        setPollingError("Polling failed to get valid game data.");
      }
    } catch (err) {
      console.error("Error during game data polling:", err);
      setPollingError("Polling failed. Will retry.");
      // Decide if polling should stop on error or just keep trying
    }
    // No finally block needed as we don't have a specific loading state for polls
  }, [gameId]);

  // ---> useEffect HOOK ONLY FOR POLLING <---
  useEffect(() => {
    if (!gameId) return; // Don't start polling without an ID

    console.log(`GameDisplay: Starting polling for gameId ${gameId}`);
    const intervalId = setInterval(() => {
      fetchGameUpdates();
    }, 5000);

    return () => {
      console.log(`GameDisplay: Stopping polling for gameId ${gameId}`);
      clearInterval(intervalId);
    };
    // Depend only on fetchGameUpdates (which depends on gameId)
  }, [fetchGameUpdates, gameId]);

  // ---> Update state if the initialGame prop changes <---
  useEffect(() => {
    setGame(initialGame);
    // Reset polling error if the initial game changes (e.g., navigating between games)
    setPollingError(null);
  }, [initialGame]);

  // Condition to show qualified submissions
  const showQualifiedList =
    qualifiedSubmissions &&
    qualifiedSubmissions.length > 0 &&
    game?.current_state !== "LOBBY" &&
    game?.current_state !== "PRELIMINARY";

  if (game) {
    return (
      <div className="game-form">
        <div className="form-content">
          {/* Game Header Section */}
          {game?.current_state === "ACTIVE" && stepTimeRemaining !== null && (
            <>
              <span className="timer-display">
                {stepTimeRemaining}s remaining
              </span>
              <br />
              <br />
            </>
          )}
          <div className="game-header-section">
            <div className="game-title-container">
              <p className="game-title">{game.name || `Game ${gameId}`}</p>
            </div>
          </div>

          {/* Game Description Section */}
          <div className="game-description-section">
            <div className="description-content">
              {game.description || "(No description provided)"}
            </div>
          </div>

          {/* Qualified Submissions Section */}
          {showQualifiedList && (
            <div className="qualified-section">
              {qualifiedSubmissions.length === 0 ? (
                <p className="no-qualified">No qualified statements yet.</p>
              ) : (
                <div className="qualified-container">
                  <h4 className="qualified-title">
                    Qualified Statements from All Rounds
                  </h4>
                  <ul className="qualified-list">
                    {qualifiedSubmissions.map((sub) => (
                      <li key={sub.id} className="qualified-item">
                        <div className="qualified-content">
                          <div className="qualified-text">
                            {sub.text_content}
                          </div>
                          <div className="qualified-meta">
                            Round {sub.submitted_in_round}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Game Configuration Section */}
          <div className="game-configuration-section">
            <div className="settings-grid">
              <div className="setting-item">
                <span className="setting-label">Total Rounds:</span>
                <span className="setting-value">{game.rounds}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Steps per Round:</span>
                <span className="setting-value">{game.steps}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Step Duration:</span>
                <span className="setting-value">{game.step_time} seconds</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Starting Resources:</span>
                <span className="setting-value">
                  {game.start_resources} per category
                </span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Bonus Resources:</span>
                <span className="setting-value">
                  {game.extra_resources} per round
                </span>
              </div>
              {game.max_statements && (
                <div className="setting-item">
                  <span className="setting-label">Max Statements:</span>
                  <span className="setting-value">
                    {game.max_statements} per round
                  </span>
                </div>
              )}
              {game.resource_categories && (
                <div className="setting-item">
                  <span className="setting-label">Resource Categories:</span>
                  <span className="setting-value">
                    {game.resource_categories.join(", ")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* QR Code Section */}
          <div className="game-actions">
            <button
              onClick={() => setShowQr(!showQr)}
              className="button button-primary"
              title="Show/Hide QR Code"
            >
              <FaQrcode /> {showQr ? "Hide QR" : "Show QR"}
            </button>
          </div>
          {showQr && (
            <div className="qr-code-section">
              <div className="qr-code-container">
                <QRCodeSVG value={gameUrl} size={200} includeMargin={true} />
                <p className="join-url">{gameUrl}</p>
              </div>
            </div>
          )}

          {/* Polling Error Display */}
          {pollingError && (
            <div className="error-section">
              <p className="error">{pollingError}</p>
            </div>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div className="game-form">
        <div className="form-content">
          <div className="error-section">
            <p className="error">No game information available</p>
          </div>
        </div>
      </div>
    );
  }
}

export default GameDisplay;
